import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { commissionsApi } from "@/api/commissions";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { currency } from "@/components/commissions/formatters";

/**
 * SP-5 WS-4 — the Pool F teaching state. A pool can RECEIVE income before
 * anyone is configured to earn from it (Pool F's allocation roster is a deferred
 * product decision). That income must never accrue silently: this banner names
 * every income-bearing pool that has no allocation roster yet, shows how much is
 * accruing unallocated, and teaches what configuring allocations will do — so
 * the roster decision is easy when the owner returns to it.
 *
 * Renders nothing when every income-bearing pool already has a roster.
 */
export function UnallocatedPoolNotice() {
  const summary = useQuery({
    queryKey: commissionKeys.poolSummary(),
    queryFn: () => commissionsApi.poolSummary(),
  });

  const pools = (summary.data ?? []).filter(
    (p) => !p.hasAllocations && p.recognizedIncome > 0,
  );
  if (pools.length === 0) return null;

  return (
    <div className="flex flex-col gap-space-2">
      {pools.map((p) => (
        <div
          key={p.poolId}
          className="flex items-start gap-space-3 p-space-4 border border-status-warning rounded-md bg-status-warning/5"
        >
          <Info size={18} strokeWidth={1.5} className="text-status-warning shrink-0 mt-[2px]" aria-hidden />
          <div>
            <div className="font-display font-bold text-body text-onyx">
              Pool {p.poolCode} · {p.poolName} is receiving income but has no allocations yet
            </div>
            <div className="text-small text-gray-mid mt-space-1">
              <span className="font-semibold text-onyx tabular-nums">
                {currency(p.unallocatedAccrual)}
              </span>{" "}
              accrued, unallocated. Income accrues to no one until an allocation roster is
              configured for this pool. Add allocations for a position to start auto-suggesting
              commissions from this income — no other change is needed.
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
